/** @jsxImportSource @gongxigames/framework/core/fragments/BuilderY */
/** @jsxRuntime automatic */

import {FC, CanBeSignals, getProp} from '@gongxigames/framework/core/fragments/BuilderY';
import {ComputedSignal, Store} from '@gongxigames/framework/core/fragments/store/Store';
import {RContainer, ResponsiveConf, ResponsiveHandler, RUnit} from '@gongxigames/framework/core/fragments/display/responsive';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {$AutoplaySettingsBase} from '@gongxigames/framework/slotV2/ui/ui-koala/modules/UIAutoplayOptions/components/$AutoplaySettingsBase';
import {cacheAsSprite} from '@gongxigames/framework/core/fragments/display/CacheAsSprite';
import { Rectangle } from 'pixi.js';

export type $AutoplaySettingsProps = CanBeSignals<{
    showOptionsPopup: boolean;
    defaultSpins: number;
    autoplayValues: number[];
    quickSpin: boolean;
    turboSpin: boolean;
    untilBonus: boolean;
    disabledLimits: boolean;
    singleWin: number | null;
    winLimit: number | null;
    lossLimit: number | null;
    onQuickSpin: (value: boolean) => void;
    onTurboSpin: (value: boolean) => void;
    onUntilBonus: (value: boolean) => void;
    onStartAutoplay: (value: number) => void;
    onUpdateValue: (value: number) => void;
    onLossLimit: (value: number | null) => void;
    onWinLimit: (value: number | null) => void;
    onSingleWin: (value: number | null) => void;
    onClose: () => void;
}>;

export const getResponsiveConfig = (disabledLimits: boolean, popup?: ResponsiveConf) => {
    let popupHeight: RUnit = '1700px';
    let optionsSelectorY: RUnit = '-120px';
    let roundsSelectorY: RUnit = '360px';

    if (disabledLimits) {
        popupHeight = '1320px';
        optionsSelectorY = '-340px';
        roundsSelectorY = '170px';
    }

    const popupPortraitConfig: ResponsiveConf = {
        scale: 0.9,
        updateHeight: true,
        updateWidth: true,
        box: {x: '50%', y: '50%', width: '950px', height: popupHeight},
        translate: {x: '-50%', y: '-50%'}
    };
    const popupLandscapeConfig: ResponsiveConf = {
        ...popupPortraitConfig,
        scale: 0.55,
        box: {x: '50%', y: '50%', width: '950px', height: popupHeight},
        translate: {x: '-50%', y: '-50%'}
    };

    const popupConfig: ComputedSignal<ResponsiveConf> = Store.computed(() => {
        if (popup) {
            return {...popup, box: {...popup.box, height: popupHeight}};
        }

        return (slotStore.stage_isPortrait.value ? popupPortraitConfig : popupLandscapeConfig);
    });
    const optionsSelectorConfig: ResponsiveConf = {box: {x: '50%', y: '50%'}, translate: {x: '-50%', y: optionsSelectorY}};
    const roundsSelectorConfig: ResponsiveConf = {box: {x: '50%', y: '50%'}, translate: { y: roundsSelectorY}};
    const autoplayButtonConfig: ResponsiveConf = {box: {x: '50%', y: '100%'}, translate: {y: '-140px'}};
    const limitsSelectorConfig: ResponsiveConf = {box: {x: '50%', y: '0'}, translate: {x: '-365px', y: '300px'}};
    const limitsSeparatorConfig: ResponsiveConf = {box: {x: '50%', y: '50%'}, translate: {y: '-250px'}};

    return {
        popup: popupConfig,
        optionsSelector: optionsSelectorConfig,
        roundsSelector: roundsSelectorConfig,
        autoplayButton: autoplayButtonConfig,
        limitsSelector: limitsSelectorConfig,
        limitsSeparator: limitsSeparatorConfig
    };
};

const POPUP_POS_CONFIG_GAME_RULES: ResponsiveConf = {
    updateHeight: true,
    updateWidth: true,
    box: {x: '0', y: '0', width: '950px', height: '1700px'},
};

export const $AutoplaySettings: FC<$AutoplaySettingsProps> = (props) => {
    const roundsCount = Store.signal(getProp(props.defaultSpins));
    const responsive = getResponsiveConfig(getProp(props.disabledLimits));
    const responsiveGameRules = getResponsiveConfig(getProp(props.disabledLimits), POPUP_POS_CONFIG_GAME_RULES);

    const gameRulesPopup = <$AutoplaySettingsBase
        roundsCount={roundsCount}
        responsiveConf={responsiveGameRules}
        {...props}
        showOptionsPopup={true}
        faderAlpha={0.0}
    /> as RContainer;

    new ResponsiveHandler(gameRulesPopup).update();

    cacheAsSprite.add({
        name: 'game_rules_AutoplayPopup',
        provider: gameRulesPopup,
        resolution: 0.5,
        region: new Rectangle(0, 0, 950, 1700),
    });

    return <$AutoplaySettingsBase
        roundsCount={roundsCount}
        responsiveConf={responsive}
        {...props}
    />;
};